import { useCallback, useRef } from 'react'
import { useReactFlow } from 'reactflow'
import { arrayMove } from './arrayMove'
import {
  type CanvasNode,
  type CanvasNodeData,
  type GroupChildNodeIds,
  computeCellSize,
  computeChildPosition,
  computeContainerSize,
  deriveGroupedNodeLayout,
  getCanvasNodeDisplayName,
  getGroupNodeData,
  normalizeLayoutMode,
  removeChildFromIds,
} from './GroupNode'
import { getGroupCategory, getMediaCategory } from './groupValidation'
import { computeInsertionIndex } from './groupLayoutUtils'
import { useGroupDropTarget, type RejectionReason } from './GroupDropTargetContext'
import { useCanvasOptional, type CanvasContextValue } from '../../CanvasContext'
import { createDebug, createNadDebug } from '@ponpon/api-client'

const dragLog = createDebug('dw:panel-drag:handler')
const groupDetectLog = createNadDebug('dw:panel-drag:group-detect')

/** Distance in pixels from group boundary at which drag-out removal commits */
const DRAG_OUT_THRESHOLD = 100

function deferredDeriveGroupedLayout(
  setNodes: (updater: (nds: CanvasNode[]) => CanvasNode[]) => void,
  groupIds: string[],
): void {
  requestAnimationFrame(() => {
    dragLog('deferredDerive groups=%o', groupIds)
    setNodes((nds) => deriveGroupedNodeLayout(nds, groupIds))
  })
}

/**
 * Hook that returns onNodeDrag and onNodeDragStop handlers for ReactFlow.
 * Detects drag-over of standalone canvas nodes onto GroupNodes, providing
 * visual feedback and handling drop absorption.
 *
 * @param existingOnNodeDragStop - The existing onNodeDragStop handler to chain
 * @param canvas - YDoc write functions for persisting group changes (updateNodeData, updateNodeParent, updateNodePosition)
 */
export function useCanvasDragToGroup(
  existingOnNodeDragStop?: (event: React.MouseEvent, node: CanvasNode, nodes: CanvasNode[]) => void,
  canvas?: Pick<
    CanvasContextValue,
    'updateNodeData' | 'updateNodeParent' | 'updateNodePosition' | 'transact'
  > | null,
) {
  const { setNodes, getNodes } = useReactFlow<CanvasNodeData>()
  const dropTargetCtx = useGroupDropTarget()
  const canvasFromContext = useCanvasOptional()
  const resolvedCanvas = canvas ?? canvasFromContext

  // Track whether we're in an active drag-to-group operation
  const activeDragRef = useRef<{ nodeId: string; nodeType: string } | null>(null)

  // Track child drag state for onNodeDragStop to consume
  const childDragStateRef = useRef<{
    sourceGroupId: string
    isOutsideParent: boolean
    targetGroupId: string | null // non-null when hovering a different group
  } | null>(null)

  // Track the last preview insertion index to avoid redundant setNodes calls
  const previewInsertionRef = useRef<number>(-1)

  const onNodeDrag = useCallback(
    (_event: React.MouseEvent, node: CanvasNode) => {
      if (!dropTargetCtx) return

      // Skip if this node is a group itself
      if (node.type === 'nodeGroup') return

      const allNodes = getNodes()

      // ── BRANCH: Child node drag (node has parentId) ──
      if (node.parentId) {
        // Signal active drag for ghost placeholder (Task 4)
        dropTargetCtx.setActiveDragNodeId(node.id)

        // Look up the parent group node
        const parentGroup = allNodes.find((n) => n.id === node.parentId)
        if (!parentGroup) return

        const parentGroupData = getGroupNodeData(parentGroup)
        const parentChildIds = parentGroupData?.in_child_node_ids || []
        const parentLayoutMode = normalizeLayoutMode(parentGroupData?.in_layout_mode)

        // Compute child's absolute center position
        // (child position is relative to parent in ReactFlow)
        const nodeWidth = node.width || 315
        const nodeHeight = node.height || 300
        const absoluteCenterX = parentGroup.position.x + node.position.x + nodeWidth / 2
        const absoluteCenterY = parentGroup.position.y + node.position.y + nodeHeight / 2

        // Compute parent group's bounding rect
        const { cellWidth: parentCellW, cellHeight: parentCellH } = computeCellSize(
          parentChildIds.filter((id): id is string => id !== null),
          allNodes,
        )
        const { width: parentGroupWidth, height: parentGroupHeight } = computeContainerSize(
          parentChildIds.length,
          parentLayoutMode,
          parentCellW,
          parentCellH,
        )

        const pgx = parentGroup.position.x
        const pgy = parentGroup.position.y

        // Check if child's center is still inside parent group bounds
        const isInsideParent =
          absoluteCenterX >= pgx &&
          absoluteCenterX <= pgx + parentGroupWidth &&
          absoluteCenterY >= pgy &&
          absoluteCenterY <= pgy + parentGroupHeight

        if (isInsideParent) {
          // ── Within-group drag: show insertion indicator ──
          const relativeX = absoluteCenterX - pgx
          const relativeY = absoluteCenterY - pgy

          const { index: insertionIndex, action: dropAction } = computeInsertionIndex(
            relativeX,
            relativeY,
            parentLayoutMode,
            parentCellW,
            parentCellH,
            parentChildIds.length,
            parentChildIds,
          )

          // ── Live reorder preview: reposition siblings to preview positions ──
          const oldIndex = parentChildIds.indexOf(node.id)
          const isFirstDragEvent = childDragStateRef.current === null

          if (isFirstDragEvent || insertionIndex !== previewInsertionRef.current) {
            // Add data-reorder-transition to sibling node wrappers (not the dragged node)
            // for CSS-driven smooth transform animation
            const parentId = node.parentId!
            for (const childId of parentChildIds) {
              if (childId === null || childId === node.id) continue
              const wrapper = document.querySelector<HTMLElement>(
                `.react-flow__node[data-id="${childId}"]`,
              )
              if (wrapper) wrapper.setAttribute('data-reorder-transition', '')
            }

            if (oldIndex !== -1) {
              // Compute preview order using arrayMove
              const previewOrder = arrayMove(parentChildIds, oldIndex, insertionIndex)

              // Reposition siblings to their preview grid positions
              setNodes((nds) =>
                nds.map((n) => {
                  if (n.id === node.id) return n // Don't touch dragged node
                  const previewIdx = previewOrder.indexOf(n.id)
                  if (previewIdx !== -1 && n.parentId === parentId) {
                    const pos = computeChildPosition(previewIdx, parentLayoutMode, parentCellW, parentCellH)
                    return { ...n, position: pos }
                  }
                  return n
                }),
              )
            }

            previewInsertionRef.current = insertionIndex
          }

          dropTargetCtx.setDropTarget({
            targetGroupId: node.parentId,
            insertionIndex,
            dropAction,
            rejection: null,
            dragOutState: null,
          })

          activeDragRef.current = { nodeId: node.id, nodeType: node.type || '' }
          childDragStateRef.current = {
            sourceGroupId: node.parentId,
            isOutsideParent: false,
            targetGroupId: null,
          }
        } else {
          // ── Child is outside parent group ──
          // Check if it's over a different group (for cross-group transfer, Task 5)
          const otherGroups = allNodes.filter(
            (n) => n.type === 'nodeGroup' && n.id !== node.parentId,
          )

          let foundOtherGroup = false
          for (const group of otherGroups) {
            const groupData = getGroupNodeData(group)
            const childIds = groupData?.in_child_node_ids || []
            const layoutMode = normalizeLayoutMode(groupData?.in_layout_mode)
            const { cellWidth, cellHeight } = computeCellSize(
              childIds.filter((id): id is string => id !== null),
              allNodes,
            )
            const { width: groupWidth, height: groupHeight } = computeContainerSize(
              childIds.length,
              layoutMode,
              cellWidth,
              cellHeight,
            )

            const gx = group.position.x
            const gy = group.position.y

            if (
              absoluteCenterX >= gx &&
              absoluteCenterX <= gx + groupWidth &&
              absoluteCenterY >= gy &&
              absoluteCenterY <= gy + groupHeight
            ) {
              // Child is over another group — category validation + insertion index
              const nodeType = node.type || ''
              const nodeCategory = getMediaCategory(nodeType)
              const groupCategory = getGroupCategory(
                childIds.filter((id): id is string => id !== null),
                (nodeId) => allNodes.find((n) => n.id === nodeId)?.type,
              )

              let rejection: RejectionReason = null
              if (nodeCategory === null) {
                rejection = 'unsupported'
              } else if (groupCategory !== null && nodeCategory !== groupCategory) {
                rejection = 'category_mismatch'
              }

              const relativeX = absoluteCenterX - gx
              const relativeY = absoluteCenterY - gy

              const { index: insertionIndex, action: dropAction } = computeInsertionIndex(
                relativeX,
                relativeY,
                layoutMode,
                cellWidth,
                cellHeight,
                childIds.length,
                childIds,
              )

              dropTargetCtx.setDropTarget({
                targetGroupId: group.id,
                insertionIndex,
                dropAction,
                rejection,
                dragOutState: null,
              })

              activeDragRef.current = { nodeId: node.id, nodeType }
              childDragStateRef.current = {
                sourceGroupId: node.parentId!,
                isOutsideParent: true,
                targetGroupId: group.id,
              }
              foundOtherGroup = true
              break
            }
          }

          if (!foundOtherGroup) {
            // Child is outside parent and not over any other group → drag-out
            // Compute distance from child center to nearest group boundary edge
            const distLeft = pgx - absoluteCenterX
            const distRight = absoluteCenterX - (pgx + parentGroupWidth)
            const distTop = pgy - absoluteCenterY
            const distBottom = absoluteCenterY - (pgy + parentGroupHeight)
            // Take the max of positive distances (how far outside)
            const distance = Math.max(distLeft, distRight, distTop, distBottom, 0)
            const distanceFactor = Math.min(distance / DRAG_OUT_THRESHOLD, 1)

            // Get node name and group name for overlay text
            const nodeName = getCanvasNodeDisplayName(node, node.type || 'Node')
            const parentGroupName = getCanvasNodeDisplayName(parentGroup, 'Group')

            dropTargetCtx.setDropTarget({
              targetGroupId: null,
              insertionIndex: 0,
              dropAction: 'insert-before',
              rejection: null,
              dragOutState: {
                sourceGroupId: node.parentId!,
                distanceFactor,
                nodeName,
                groupName: parentGroupName,
              },
            })

            activeDragRef.current = { nodeId: node.id, nodeType: node.type || '' }
            childDragStateRef.current = {
              sourceGroupId: node.parentId!,
              isOutsideParent: true,
              targetGroupId: null,
            }
          }
        }

        return // Don't fall through to standalone logic
      }

      // ── EXISTING: Standalone node drag (no parentId) ──

      // Find all group nodes on the canvas
      const groupNodes = allNodes.filter((n) => n.type === 'nodeGroup' && n.id !== node.id)

      // Get the dragged node's absolute center position
      const nodeWidth = node.width || 315
      const nodeHeight = node.height || 300
      const nodeCenterX = node.position.x + nodeWidth / 2
      const nodeCenterY = node.position.y + nodeHeight / 2

      groupDetectLog({
        event: 'standalone-drag-start',
        nodeId: node.id,
        nodeType: node.type,
        groupCount: groupNodes.length,
        nodeCenterX: Math.round(nodeCenterX),
        nodeCenterY: Math.round(nodeCenterY),
        rawWidth: node.width,
        rawHeight: node.height,
      })

      // Check each group to see if the node center is within its bounds
      let foundTarget = false
      for (const group of groupNodes) {
        const groupData = getGroupNodeData(group)
        const childIds = groupData?.in_child_node_ids || []
        const layoutMode = normalizeLayoutMode(groupData?.in_layout_mode)
        const { cellWidth, cellHeight } = computeCellSize(
          childIds.filter((id): id is string => id !== null),
          allNodes,
        )

        // Use computeContainerSize for hit-testing — always fresh from current childIds.
        // Add a small buffer to account for CSS padding/minHeight that the visual
        // container may include beyond the computed size.
        const HIT_TEST_BUFFER = 15
        const computedSize = computeContainerSize(
          childIds.length,
          layoutMode,
          cellWidth,
          cellHeight,
        )
        const groupWidth = computedSize.width + HIT_TEST_BUFFER
        const groupHeight = computedSize.height + HIT_TEST_BUFFER

        // Check if the dragged node's center is within the group's bounding rect
        const gx = group.position.x
        const gy = group.position.y

        groupDetectLog({
          event: 'group-bounds',
          groupId: group.id,
          gx: Math.round(gx),
          gy: Math.round(gy),
          groupWidth: Math.round(groupWidth),
          groupHeight: Math.round(groupHeight),
          childCount: childIds.length,
          cellWidth: Math.round(cellWidth),
          cellHeight: Math.round(cellHeight),
        })

        if (
          nodeCenterX >= gx &&
          nodeCenterX <= gx + groupWidth &&
          nodeCenterY >= gy &&
          nodeCenterY <= gy + groupHeight
        ) {
          // Skip if already a child of this group (stale parentId after restart)
          if (childIds.includes(node.id)) continue

          // Node center is over this group — determine acceptance/rejection
          const nodeType = node.type || ''
          const nodeCategory = getMediaCategory(nodeType)
          const groupCategory = getGroupCategory(
            childIds.filter((id): id is string => id !== null),
            (nodeId) => allNodes.find((n) => n.id === nodeId)?.type,
          )

          let rejection: RejectionReason = null
          if (nodeCategory === null) {
            rejection = 'unsupported'
          } else if (groupCategory !== null && nodeCategory !== groupCategory) {
            rejection = 'category_mismatch'
          }

          groupDetectLog({
            event: 'intersection-hit',
            nodeId: node.id,
            groupId: group.id,
            nodeCategory,
            groupCategory,
            rejection,
          })

          // Compute pointer position relative to the group
          const relativeX = nodeCenterX - gx
          const relativeY = nodeCenterY - gy

          const { index: insertionIndex, action: dropAction } = computeInsertionIndex(
            relativeX,
            relativeY,
            layoutMode,
            cellWidth,
            cellHeight,
            childIds.length,
            childIds,
          )

          groupDetectLog({
            event: 'drop-target-set',
            nodeId: node.id,
            targetGroupId: group.id,
            insertionIndex,
            dropAction,
          })

          dropTargetCtx.setDropTarget({
            targetGroupId: group.id,
            insertionIndex,
            dropAction,
            rejection,
          })

          activeDragRef.current = { nodeId: node.id, nodeType }
          foundTarget = true
          break // Only one target at a time
        }
      }

      if (!foundTarget) {
        // Not over any group — clear target
        if (dropTargetCtx.dropTarget.targetGroupId !== null) {
          dropTargetCtx.clearDropTarget()
        }
        activeDragRef.current = null
        groupDetectLog({
          event: 'no-target-found',
          nodeId: node.id,
          nodeCenterX: Math.round(nodeCenterX),
          nodeCenterY: Math.round(nodeCenterY),
        })
      }
    },
    [getNodes, setNodes, dropTargetCtx],
  )

  const onNodeDragStop = useCallback(
    (event: React.MouseEvent, node: CanvasNode, draggedNodes: CanvasNode[]) => {
      // ── Within-group reorder (child node dropped back into its parent group) ──
      if (node.parentId && childDragStateRef.current) {
        const parentGroupId = node.parentId
        const childState = childDragStateRef.current

        dragLog('dragStop child=%s parent=%s pos=(%d,%d) isOutside=%s targetGroup=%s',
          node.id, parentGroupId, node.position.x, node.position.y,
          childState.isOutsideParent, childState.targetGroupId)

        // Use childDragStateRef (synchronous ref) to decide the branch,
        // not dropTargetCtx.dropTarget (React state which may be stale).
        if (!childState.isOutsideParent &&
          dropTargetCtx &&
          dropTargetCtx.dropTarget.targetGroupId === parentGroupId &&
          dropTargetCtx.dropTarget.rejection === null
        ) {
          const allNodes = getNodes()
          const groupNode = allNodes.find((n) => n.id === parentGroupId)
          if (groupNode) {
            const groupData = getGroupNodeData(groupNode)
            const childIds = groupData?.in_child_node_ids || []
            const layoutMode = normalizeLayoutMode(groupData?.in_layout_mode)

            const oldIndex = childIds.indexOf(node.id)
            const newIndex = dropTargetCtx.dropTarget.insertionIndex
            const dropAction = dropTargetCtx.dropTarget.dropAction

            if (oldIndex !== -1) {
              if (dropAction === 'replace' && newIndex < childIds.length && childIds[newIndex] === null) {
                // Replace empty slot: move child to the null slot, leave old position as null
                const newOrder: GroupChildNodeIds = [...childIds]
                newOrder[oldIndex] = null
                newOrder[newIndex] = node.id
                // Trim trailing nulls
                while (newOrder.length > 0 && newOrder[newOrder.length - 1] === null) {
                  newOrder.pop()
                }

                const nonNullIds = newOrder.filter((id): id is string => id !== null)
                const { cellWidth, cellHeight } = computeCellSize(nonNullIds, allNodes)
                const { width, height } = computeContainerSize(
                  newOrder.length,
                  layoutMode,
                  cellWidth,
                  cellHeight,
                )

                setNodes((nds) =>
                  deriveGroupedNodeLayout(
                    nds.map((n) => {
                      if (n.id === parentGroupId) {
                        return {
                          ...n,
                          data: { ...n.data, in_child_node_ids: newOrder },
                          style: { ...n.style, width, height },
                        }
                      }
                      return n
                    }),
                    [parentGroupId],
                  ),
                )

                resolvedCanvas?.updateNodeData(parentGroupId, { in_child_node_ids: newOrder })
                deferredDeriveGroupedLayout(setNodes, [parentGroupId])
                dragLog('replaceSlot child=%s oldIndex=%d newIndex=%d',
                  node.id, oldIndex, newIndex)
              } else if (oldIndex !== newIndex) {
                // Reorder: position actually changed
                const newOrder = arrayMove(childIds, oldIndex, newIndex)

                const nonNullIds = newOrder.filter((id): id is string => id !== null)
                const { cellWidth, cellHeight } = computeCellSize(nonNullIds, allNodes)
                const { width, height } = computeContainerSize(
                  newOrder.length,
                  layoutMode,
                  cellWidth,
                  cellHeight,
                )

                setNodes((nds) =>
                  deriveGroupedNodeLayout(
                    nds.map((n) => {
                      if (n.id === parentGroupId) {
                        return {
                          ...n,
                          data: { ...n.data, in_child_node_ids: newOrder },
                          style: { ...n.style, width, height },
                        }
                      }
                      return n
                    }),
                    [parentGroupId],
                  ),
                )

                resolvedCanvas?.updateNodeData(parentGroupId, { in_child_node_ids: newOrder })
                deferredDeriveGroupedLayout(setNodes, [parentGroupId])
                dragLog('reorder child=%s oldIndex=%d newIndex=%d',
                  node.id, oldIndex, newIndex)
              } else {
                // Same position — snap child back to grid position (no reorder needed)
                const nonNullIds = childIds.filter((id): id is string => id !== null)
                const { cellWidth, cellHeight } = computeCellSize(nonNullIds, allNodes)
                const expectedPos = computeChildPosition(oldIndex, layoutMode, cellWidth, cellHeight)
                dragLog('snapBack child=%s oldIndex=%d dragPos=(%d,%d) gridPos=(%d,%d)',
                  node.id, oldIndex, node.position.x, node.position.y, expectedPos.x, expectedPos.y)
                setNodes((nds) =>
                  deriveGroupedNodeLayout(nds, [parentGroupId]),
                )
                deferredDeriveGroupedLayout(setNodes, [parentGroupId])
              }
            }
          }
        } else if (
          childState.isOutsideParent &&
          childState.targetGroupId &&
          dropTargetCtx &&
          dropTargetCtx.dropTarget.targetGroupId === childState.targetGroupId &&
          dropTargetCtx.dropTarget.rejection === null
        ) {
          // ── Cross-group transfer: child dropped on a compatible different group ──
          const allNodes = getNodes()
          const sourceGroupId = childState.sourceGroupId
          const targetGId = childState.targetGroupId
          const crossInsertionIndex = dropTargetCtx.dropTarget.insertionIndex

          const sourceGroup = allNodes.find((n) => n.id === sourceGroupId)
          const targetGroup = allNodes.find((n) => n.id === targetGId)
          if (sourceGroup && targetGroup) {
            const sourceData = getGroupNodeData(sourceGroup)
            const targetData = getGroupNodeData(targetGroup)

            const sourceChildIds = sourceData?.in_child_node_ids || []
            const targetChildIds = targetData?.in_child_node_ids || []

            // 1. Remove from source (always leaves gap)
            const newSourceIds = removeChildFromIds(node.id, sourceChildIds)

            // 2. Insert into target at the insertion index from drop target context
            const newTargetIds = [...targetChildIds]
            newTargetIds.splice(crossInsertionIndex, 0, node.id)

            // 3. Compute layout for source group
            const sourceLayoutMode = normalizeLayoutMode(sourceData?.in_layout_mode)
            const sourceNonNullIds = newSourceIds.filter((id): id is string => id !== null)
            const { cellWidth: srcCellW, cellHeight: srcCellH } = computeCellSize(sourceNonNullIds, allNodes)
            const { width: srcWidth, height: srcHeight } = computeContainerSize(
              newSourceIds.length,
              sourceLayoutMode,
              srcCellW,
              srcCellH,
            )

            // 4. Compute layout for target group
            const targetLayoutMode = normalizeLayoutMode(targetData?.in_layout_mode)
            const targetNonNullIds = newTargetIds.filter((id): id is string => id !== null)
            const { cellWidth: tgtCellW, cellHeight: tgtCellH } = computeCellSize(targetNonNullIds, allNodes)
            const { width: tgtWidth, height: tgtHeight } = computeContainerSize(
              newTargetIds.length,
              targetLayoutMode,
              tgtCellW,
              tgtCellH,
            )

            // 5. Atomic setNodes: update both groups + transferred child + reposition all children
            setNodes((nds) =>
              deriveGroupedNodeLayout(
                nds.map((n) => {
                  if (n.id === sourceGroupId) {
                    return {
                      ...n,
                      data: { ...n.data, in_child_node_ids: newSourceIds },
                      style: { ...n.style, width: srcWidth, height: srcHeight },
                    }
                  }
                  if (n.id === targetGId) {
                    return {
                      ...n,
                      data: { ...n.data, in_child_node_ids: newTargetIds },
                      style: { ...n.style, width: tgtWidth, height: tgtHeight },
                    }
                  }
                  if (n.id === node.id) {
                    return { ...n, parentId: targetGId }
                  }
                  return n
                }),
                [sourceGroupId, targetGId],
              ),
            )

            // 6. Persist atomically so only one observer rebuild fires
            const writeAll = () => {
              resolvedCanvas?.updateNodeParent(node.id, targetGId)
              resolvedCanvas?.updateNodeData(sourceGroupId, { in_child_node_ids: newSourceIds })
              resolvedCanvas?.updateNodeData(targetGId, { in_child_node_ids: newTargetIds })
            }
            if (resolvedCanvas?.transact) {
              resolvedCanvas.transact(writeAll)
            } else {
              writeAll()
            }
            deferredDeriveGroupedLayout(setNodes, [sourceGroupId, targetGId])
          }
        } else if (childState.isOutsideParent && !childState.targetGroupId) {
          // ── Drag-out removal: outside parent, no cross-group target ──
          dragLog('dragOut ENTERED child=%s sourceGroup=%s', node.id, childState.sourceGroupId)
          const allNodes = getNodes()
          const sourceGroup = allNodes.find((n) => n.id === childState.sourceGroupId)
          dragLog('dragOut sourceGroup=%s found=%s', childState.sourceGroupId, !!sourceGroup)
          if (sourceGroup) {
            const sourceData = getGroupNodeData(sourceGroup)
            const sourceChildIds = sourceData?.in_child_node_ids || []
            const layoutMode = normalizeLayoutMode(sourceData?.in_layout_mode)

            // 1. Remove child from in_child_node_ids (always leaves gap)
            const newChildIds = removeChildFromIds(node.id, sourceChildIds)

            // 2. Compute absolute canvas position
            const absoluteX = sourceGroup.position.x + node.position.x
            const absoluteY = sourceGroup.position.y + node.position.y

            // 3. Compute layout for remaining children
            const nonNullIds = newChildIds.filter((id): id is string => id !== null)
            const { cellWidth, cellHeight } = computeCellSize(nonNullIds, allNodes)
            const { width, height } = computeContainerSize(
              nonNullIds.length > 0 ? newChildIds.length + 1 : 0,
              layoutMode,
              cellWidth,
              cellHeight,
            )

            // 4. Atomic setNodes: detach child, update group, reposition remaining
            setNodes((nds) =>
              deriveGroupedNodeLayout(
                nds.map((n) => {
                  if (n.id === node.id) {
                    return {
                      ...n,
                      parentId: undefined,
                      extent: undefined,
                      position: { x: absoluteX, y: absoluteY },
                    }
                  }
                  if (n.id === childState.sourceGroupId) {
                    return {
                      ...n,
                      data: { ...n.data, in_child_node_ids: newChildIds },
                      style: { ...n.style, width, height },
                    }
                  }
                  return n
                }),
                [childState.sourceGroupId],
              ),
            )

            // 5. Persist to YDoc atomically so only one observer rebuild fires
            const writeAll = () => {
              resolvedCanvas?.updateNodeParent(node.id, null)
              resolvedCanvas?.updateNodePosition(node.id, absoluteX, absoluteY)
              resolvedCanvas?.updateNodeData(childState.sourceGroupId, { in_child_node_ids: newChildIds })
            }
            if (resolvedCanvas?.transact) {
              resolvedCanvas.transact(writeAll)
            } else {
              writeAll()
            }
            deferredDeriveGroupedLayout(setNodes, [childState.sourceGroupId])

            // 6. Trigger "Removed..." flash
            const nodeName = getCanvasNodeDisplayName(node, node.type || 'Node')
            const groupName = getCanvasNodeDisplayName(sourceGroup, 'Group')

            dropTargetCtx?.setRemovedFlash({
              sourceGroupId: childState.sourceGroupId,
              nodeName,
              groupName,
            })
          }
        }

        childDragStateRef.current = null
        previewInsertionRef.current = -1
        activeDragRef.current = null

        // Clean up data-reorder-transition from all child node wrappers
        document
          .querySelectorAll('.react-flow__node[data-reorder-transition]')
          .forEach((el) => el.removeAttribute('data-reorder-transition'))

        dropTargetCtx?.clearDropTarget()
        dropTargetCtx?.setActiveDragNodeId(null)
        dragLog('chainToBase child=%s pos=(%d,%d) isOutside=%s targetGroup=%s dropTargetGroupId=%s — base handler will write this position to YDoc',
          node.id, node.position.x, node.position.y,
          childState.isOutsideParent, childState.targetGroupId,
          dropTargetCtx?.dropTarget.targetGroupId)
        existingOnNodeDragStop?.(event, node, draggedNodes)
        return
      }

      if (dropTargetCtx && dropTargetCtx.dropTarget.targetGroupId && activeDragRef.current) {
        const { targetGroupId, insertionIndex, dropAction, rejection } = dropTargetCtx.dropTarget

        groupDetectLog({
          event: 'dragstop-standalone',
          nodeId: node.id,
          targetGroupId,
          insertionIndex,
          dropAction,
          rejection,
          activeDragNodeId: activeDragRef.current?.nodeId,
        })

        // Only absorb if not rejected
        if (rejection === null) {
          const allNodes = getNodes()
          const groupNode = allNodes.find((n) => n.id === targetGroupId)
          if (groupNode) {
            const groupData = getGroupNodeData(groupNode)
            const childIds = groupData?.in_child_node_ids || []

            // Guard: skip if already a child (prevents duplicate from stale parentId)
            if (childIds.includes(node.id)) {
              groupDetectLog({
                event: 'absorb-skipped-duplicate',
                nodeId: node.id,
                targetGroupId,
              })
              dropTargetCtx.clearDropTarget()
              activeDragRef.current = null
              existingOnNodeDragStop?.(event, node, draggedNodes)
              return
            }

            // Apply the drop action
            const newIds = [...childIds]
            if (dropAction === 'replace' && insertionIndex < newIds.length && newIds[insertionIndex] === null) {
              // Replace empty slot
              newIds[insertionIndex] = node.id
            } else {
              // Insert before/after
              newIds.splice(insertionIndex, 0, node.id)
            }

            groupDetectLog({
              event: 'absorb-start',
              nodeId: node.id,
              targetGroupId,
              insertionIndex,
              newChildIds: newIds,
            })

            // Atomic setNodes: set parentId on dragged node, reposition all children,
            // update group's in_child_node_ids and container size
            setNodes((nds) =>
              deriveGroupedNodeLayout(
                nds.map((n) => {
                  if (n.id === node.id) {
                    return { ...n, parentId: targetGroupId }
                  }
                  if (n.id === targetGroupId) {
                    return { ...n, data: { ...n.data, in_child_node_ids: newIds } }
                  }
                  return n
                }),
                [targetGroupId],
              ),
            )

            // Persist to YDoc atomically so only one observer rebuild fires
            const writeAll = () => {
              resolvedCanvas?.updateNodeParent(node.id, targetGroupId)
              resolvedCanvas?.updateNodeData(targetGroupId, { in_child_node_ids: newIds })
            }
            if (resolvedCanvas?.transact) {
              resolvedCanvas.transact(writeAll)
            } else {
              writeAll()
            }
            deferredDeriveGroupedLayout(setNodes, [targetGroupId])

            groupDetectLog({
              event: 'absorb-complete',
              nodeId: node.id,
              targetGroupId,
              newChildIds: newIds,
            })

            // Clear the drop target
            dropTargetCtx.clearDropTarget()
            activeDragRef.current = null

            // Don't call existing handler since we handled the drop
            return
          }
        }

        // If rejected, clear and fall through to existing handler
        groupDetectLog({
          event: 'absorb-rejected',
          nodeId: node.id,
          rejection,
        })
        dropTargetCtx.clearDropTarget()
        activeDragRef.current = null
      }

      // Chain to existing handler for normal drag stops
      existingOnNodeDragStop?.(event, node, draggedNodes)
    },
    [dropTargetCtx, getNodes, setNodes, resolvedCanvas, existingOnNodeDragStop],
  )

  return { onNodeDrag, onNodeDragStop }
}
